/**
 * What a project has cost in provider fees.
 *
 * Two rules shape this. Quantities are recorded alongside the money, because
 * published prices change and a stored figure would quietly become a lie —
 * with the characters and minutes kept, any past project can be recosted. And
 * rates are configuration rather than constants, because they differ by plan:
 * the same 10,000 characters of speech are free on one ElevenLabs tier and
 * chargeable on another.
 *
 * The result is an estimate and is labelled as one wherever it is shown. It
 * counts the calls this application makes; it does not know about a provider's
 * minimum billing increments, free monthly allowances or taxes.
 */

export const usageProviders = ["openai", "anthropic", "gemini", "openrouter", "elevenlabs"] as const;
export type UsageProvider = (typeof usageProviders)[number];

export const usageKinds = ["transcription", "script", "voice", "vision"] as const;
export type UsageKind = (typeof usageKinds)[number];

/**
 * What a call was for.
 *
 * Finer than `kind`, and needed because script writing and cut planning are
 * both `script`, billed to the same model, and otherwise impossible to tell
 * apart in the ledger — which makes "what is the cut planner costing me?"
 * unanswerable, and that is exactly the question worth asking before paying for
 * a stronger one.
 */
export const usageOperations = [
  "transcription",
  "video_analysis",
  "script_generation",
  "cut_planning",
  "narration",
  /** A finished video's narration put into another language. */
  "translation",
] as const;
export type UsageOperation = (typeof usageOperations)[number];

export type UsageUnit = "minutes" | "tokens" | "characters" | "frames";

export interface ProviderUsage {
  provider: UsageProvider;
  kind: UsageKind;
  operation?: UsageOperation;
  /** The job that made the call, so a cost traces back to a run. */
  jobId?: string;
  /** The provider's model name, when there is one, for auditing a bill. */
  model?: string;
  /** Billed amount in `unit`. For token models this is input + output. */
  quantity: number;
  unit: UsageUnit;
  inputTokens?: number;
  outputTokens?: number;
}

export interface Rates {
  /** OpenAI Whisper, US dollars per minute of audio. */
  whisperPerMinute: number;
  /** ElevenLabs, dollars per 1,000 characters. Zero on the free tier. */
  elevenLabsPer1kCharacters: number;
  openaiInputPer1mTokens: number;
  openaiOutputPer1mTokens: number;
  anthropicInputPer1mTokens: number;
  anthropicOutputPer1mTokens: number;
  geminiInputPer1mTokens: number;
  geminiOutputPer1mTokens: number;
  /**
   * OpenRouter bills at whatever the routed model costs, which this cannot
   * know. Left at the OpenAI rate as a placeholder so a figure appears at all;
   * anyone using it seriously should set the real one.
   */
  openrouterInputPer1mTokens: number;
  openrouterOutputPer1mTokens: number;
}

/**
 * List prices at the time of writing, in US dollars.
 *
 * Deliberately overridable per deployment — see `ratesFromEnv`. Anyone on a
 * committed-use discount, a free tier or a different model should set their
 * own rather than trust these.
 */
export const defaultRates: Rates = {
  whisperPerMinute: 0.006,
  elevenLabsPer1kCharacters: 0.3,
  openaiInputPer1mTokens: 0.15,
  openaiOutputPer1mTokens: 0.6,
  anthropicInputPer1mTokens: 5,
  anthropicOutputPer1mTokens: 25,
  geminiInputPer1mTokens: 1.25,
  geminiOutputPer1mTokens: 10,
  openrouterInputPer1mTokens: 0.15,
  openrouterOutputPer1mTokens: 0.6,
};

const numberFromEnv = (value: string | undefined, fallback: number): number => {
  if (value === undefined || value.trim() === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

export function ratesFromEnv(env: Record<string, string | undefined>): Rates {
  return {
    whisperPerMinute: numberFromEnv(env.RATE_WHISPER_PER_MINUTE, defaultRates.whisperPerMinute),
    elevenLabsPer1kCharacters: numberFromEnv(env.RATE_ELEVENLABS_PER_1K_CHARS, defaultRates.elevenLabsPer1kCharacters),
    openaiInputPer1mTokens: numberFromEnv(env.RATE_OPENAI_INPUT_PER_1M, defaultRates.openaiInputPer1mTokens),
    openaiOutputPer1mTokens: numberFromEnv(env.RATE_OPENAI_OUTPUT_PER_1M, defaultRates.openaiOutputPer1mTokens),
    anthropicInputPer1mTokens: numberFromEnv(env.RATE_ANTHROPIC_INPUT_PER_1M, defaultRates.anthropicInputPer1mTokens),
    anthropicOutputPer1mTokens: numberFromEnv(
      env.RATE_ANTHROPIC_OUTPUT_PER_1M,
      defaultRates.anthropicOutputPer1mTokens,
    ),
    geminiInputPer1mTokens: numberFromEnv(env.RATE_GEMINI_INPUT_PER_1M, defaultRates.geminiInputPer1mTokens),
    geminiOutputPer1mTokens: numberFromEnv(env.RATE_GEMINI_OUTPUT_PER_1M, defaultRates.geminiOutputPer1mTokens),
    openrouterInputPer1mTokens: numberFromEnv(
      env.RATE_OPENROUTER_INPUT_PER_1M,
      defaultRates.openrouterInputPer1mTokens,
    ),
    openrouterOutputPer1mTokens: numberFromEnv(
      env.RATE_OPENROUTER_OUTPUT_PER_1M,
      defaultRates.openrouterOutputPer1mTokens,
    ),
  };
}

/** Dollars for one recorded call. Rounded to the cent-thousandth, not the cent. */
export function estimateCostUsd(usage: ProviderUsage, rates: Rates = defaultRates): number {
  const round = (value: number) => Number(Math.max(0, value).toFixed(5));

  if (usage.unit === "minutes") return round(usage.quantity * rates.whisperPerMinute);
  if (usage.unit === "characters") return round((usage.quantity / 1000) * rates.elevenLabsPer1kCharacters);

  // Frames are counted, not priced. A visual call is billed by the provider in
  // tokens it does not report back here, and inventing a per-frame rate would
  // put a confident wrong number in the ledger. The count is recorded so the
  // call is visible and can be costed later; the money column stays honest by
  // staying empty.
  if (usage.unit === "frames") return 0;

  // Token pricing is split by direction, so a usage that only reports a total
  // is charged at the input rate — the cheaper of the two, which keeps an
  // estimate from overstating a bill it cannot see.
  const input = usage.inputTokens ?? usage.quantity;
  const output = usage.outputTokens ?? 0;
  const perMillion = {
    anthropic: { in: rates.anthropicInputPer1mTokens, out: rates.anthropicOutputPer1mTokens },
    gemini: { in: rates.geminiInputPer1mTokens, out: rates.geminiOutputPer1mTokens },
    openrouter: { in: rates.openrouterInputPer1mTokens, out: rates.openrouterOutputPer1mTokens },
    openai: { in: rates.openaiInputPer1mTokens, out: rates.openaiOutputPer1mTokens },
    elevenlabs: { in: rates.openaiInputPer1mTokens, out: rates.openaiOutputPer1mTokens },
  }[usage.provider];

  return round((input / 1_000_000) * perMillion.in + (output / 1_000_000) * perMillion.out);
}

/**
 * Money, for a reader.
 *
 * Two decimals, because that is what money has. A third told the truth about
 * "$0.278" and cost more than it was worth: nobody bills a third of a cent, and
 * a column of three-decimal figures is read as a measurement rather than as a
 * price.
 *
 * Sub-cent totals are the norm for a short project, and those keep their own
 * answer. Rounding them to "$0.00" would say the pipeline was free, when the
 * point of showing a cost at all is that it is not — so they are reported as
 * less than a cent instead, which is both true and to two decimals.
 */
export function formatUsd(amount: number): string {
  if (!Number.isFinite(amount) || amount <= 0) return "$0.00";
  if (amount < 0.01) return `<$0.01`;
  return `$${amount.toFixed(2)}`;
}
